/* eslint-disable no-console */
import { WrapFilter } from './styles';

import Animation from 'services/utils/Animation';

import Mobile from '../../../Mobile';
import Navigation from '../../../Navigation';
import MultiSelect from '../MultiSelect';

import Icon from '@map/components/Icons';
import Button from '@material-ui/core/Button';
import Input from '@material-ui/core/Input';
/**
 * useState é um hook que nos permite rastrear o estado em um
 * componente de função, o estado geralmente se refere a dados ou
 * propriedads que precisam ser rastreador em um aplicativo.
 * 
 * useEffect é um hook que permite executar efeitos colaterais em
 * seus componentes, por exemplo: buscar dados, atualizar diretamente
 * o DOM e cronômetros, ele aceita dois argumentos o segundo é opcional.
 * 
 * useCallback é um hook que retorna uma função de retorno
 * de chamada memorizada
 * 
 * useRef permite que persista valores entre renderizações, ele pode
 * ser usado para armazenar um valor mutável que não causa uma nova
 * renderização quando atualizado e pode ser usado para acessar di-
 * retamente um elemento do DOM.
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Filter
 * 
 * @param {object} columns
 * @param {string} layerType
 * @param {string} manualOperator
 * @param {object} operators
 * @param {number} initialValues
 * @param {boolean} onFilter
 * @param {boolean} onClose
 * @param {function} getDistinctValues
 * @returns 
 */
const Filter = ({
  columns,
  layerType,  
  manualOperator,
  operators,
  initialValues,
  onFilter,
  onClose,
  getDistinctValues,
}) => {
  const [activeView, setActiveView] = useState(0);
  const flex = useRef();
  const stringOperatorsList = useMemo(
    () => ([
      { label: "=", value: "=" },
      { label: "Contém", value: "LIKE" },
    ]),
    []
  );
  const integerOperatorsList = useMemo(
    () => ([
      { label: "=", value: "=" },
      { label: "!==", value: "!==" },    
      { label: "<", value: "<" },
      { label: "<=", value: "<=" },
      { label: ">", value: ">" },    
      { label: ">=", value: ">=" },      
    ]),
    []
  );
  const [displayedColumns, setDisplayedColumns] = useState([]);
  const [enabledFilter, setEnabledFilter] = useState(false)
  const [qtdDisplayedColumns, setQtdDisplayedColumns] = useState(0)
  const [filterColumn, setFilterColumn] = useState(null);
  // Se houve operador manual predefinido, 
  // define o mesmo como selecionado
  const [filterOperator, setFilterOperator] = useState(
    manualOperator
      ? manualOperator
      : 'LIKE'
  );  
  // Define lista de operadores de acordo com o
  // operador manual predefinido (se houver)
  // verificando se o mesmo existe na lista
  // para que não quebre a apĺicação
  const [operatorsList, setOperatorsList] = useState(
    filterOperator && integerOperatorsList.find((o) => o.value === filterOperator) !== undefined
      ? integerOperatorsList
      : stringOperatorsList
  );  
  const [filterValues, setFilterValues] = useState([]);
  const [filterData, setFilterData] = useState([]);
  const [expandMobileValues, setExpandMobileValues] = useState(false);
  const handleFilter = () => {    
    onFilter && onFilter(displayedColumns, filterColumn, filterOperator, filterValues);
    onClose && onClose();
  };
  const moveTo = useCallback(
    (index) => (e) => {
      setActiveView(index);
    },
    [setActiveView]
  );

  /**
   * Ação de animação até o step ativo activeView
   */
  const scrollStep = useCallback(() => {    
    if (flex.current && window.innerWidth <= 768) {
      let scrollLeft = flex.current.scrollLeft;
      let width = flex.current.offsetWidth;
      const animation = new Animation(
        500,
        (stepNumber) => {
          flex.current.scrollLeft =
            scrollLeft * (1 - stepNumber) + activeView * width * stepNumber;
        },
        () => {
          flex.current.scrollLeft = activeView * width;
        }
      );
      animation.start();
    }
  }, [activeView, flex]);

  useEffect(() => {
    
    if(filterColumn === null && !filterValues.length && qtdDisplayedColumns === displayedColumns.length){
      setEnabledFilter(false)
    }else if(filterColumn !== null && filterValues){
      setEnabledFilter(true)
    }else if(filterColumn !== null && qtdDisplayedColumns !== displayedColumns.length){
      setEnabledFilter(false)
    }else if(qtdDisplayedColumns !== displayedColumns.length){
      setEnabledFilter(true)
    }else{
      setEnabledFilter(false) 
    } 

  }, [displayedColumns, qtdDisplayedColumns, filterColumn, filterValues]);

  // Escuta as alterações em activeView para fazer andar as tabs
  useEffect(() => {
    if (typeof activeView !== 'undefined') {
      scrollStep();
    }
  }, [activeView, scrollStep]);

  useEffect(() => {
    if (initialValues) {
      if (initialValues.displayedColumns) {
        setDisplayedColumns(initialValues.displayedColumns);
        setQtdDisplayedColumns(initialValues.displayedColumns.length);
      } else {
        setQtdDisplayedColumns(0); // Ou outro valor padrão
      }
      initialValues.filterColumn && setFilterColumn(initialValues.filterColumn);
      initialValues.filterValues && setFilterValues(initialValues.filterValues);
    }
  }, [initialValues]);

  useEffect(() => {
    if (filterColumn) {
      if (!['importada', 'criada'].includes(layerType)) {
        setFilterData([]);
        let op = 'LIKE';
        if (operators && operators.length) {
          op = operators.find((o) => o.atributo === filterColumn).operador;
        }
        setFilterOperator(op);
        if (op === 'IN') {
          getDistinctValues(filterColumn).then((attributes) =>
            setFilterData(
              attributes.map((a) => ({
                label: a[filterColumn],
                value: a[filterColumn],
              }))
            )
          );
        }
      } else {              
        let columnType = columns.find((col) => col.name === filterColumn).type;  
        if (columnType === "integer") {
          setOperatorsList(integerOperatorsList);           
        } else if (columnType === "string") {
          setOperatorsList(stringOperatorsList);                  
        }  
      }      
    }
  }, [filterColumn, getDistinctValues, operators, layerType, columns, integerOperatorsList, stringOperatorsList, filterOperator, manualOperator]);

  const views = [
    {
      title: 'Ocultar coluna',
      valid: true,
    },
    {
      title: 'Atributo',
      valid: true,
      bgColor: '#C24938',
    },
    {
      title: 'Valor / Feição',
      valid: true,
      bgColor: '#32B797',
    },
  ];

  /**
   * @return Um menu onde é possivel filtrar por coluna ou atributo,
   * valor ou feição e ocultar coluna.
   */
  return (
    <WrapFilter>
      <Mobile>
        <Navigation views={views} activeView={activeView} moveTo={moveTo} />
      </Mobile>
      <div className="filter-container" ref={flex}>
        {/* {views.map(({ Component }) => (
          <div className="filter-field">{Component}</div>
        ))} */}
        <div className="filter-field">
          <div className="field">
            <MultiSelect
              type="checkbox"
              placeholder="Mostrar/Ocultar coluna"
              name="hide-column"
              value={displayedColumns}
              onChange={(v) => setDisplayedColumns(v)}
              data={
                columns &&
                columns.length > 0 &&
                columns
                  .filter((c) => c.name !== 'bbox')
                  .map((c) => ({ label: c.label, value: c.name }))
              }
            />
          </div>
        </div>
        <div className="filter-field">
          {filterColumn && (
            <div className={`values ${expandMobileValues && 'expanded'}`}>
              <button
                className="expand-button"
                onClick={() => setExpandMobileValues(!expandMobileValues)}
              >
                <Icon i="map-attributetable-chevrondown" />
              </button>
              <div className="item-value red">
                <p>{filterColumn}</p>
              </div>
              {Array.isArray(filterValues) &&
                filterValues.map((fv, i) => (
                  <div className="item-value" key={i}>
                    <p>{fv}</p>
                  </div>
                ))}
              {filterValues && typeof filterValues === 'string' && (
                <div className="item-value">
                  <p>{filterValues}</p>
                </div>
              )}
            </div>
          )}
          <div className="field">
            <MultiSelect
              type="radio"
              placeholder="Coluna / Atributo"
              name="attribute"
              value={filterColumn}
              onChange={(v) => {    
                
                setFilterColumn(v);                
                
                let op = 'LIKE';
                if (operators && operators.length) {
                  op = operators.find((o) => o.atributo === v).operador;
                }
                if (op === 'IN') {
                  setFilterValues([]);
                } else if (op === 'LIKE') {
                  setFilterValues('');
                }


                let columnType = columns.find((col) => col.name === v).type;
                if (columnType === "integer") {
                  setOperatorsList(integerOperatorsList);
                  op = '=';  
                } else if (columnType === "string") {
                  setOperatorsList(stringOperatorsList);                  
                }                
                setFilterOperator(op);
              }}
              data={
                columns &&
                columns.length > 0 &&
                columns
                  .filter(
                    (c) =>
                      c.name !== 'bbox' &&
                      (operators?.length
                        ? operators.find((o) => o.atributo === c.name)
                        : true) // &&
                        // displayedColumns.includes(c.name)
                  )
                  .map((c) => ({ label: c.label, value: c.name }))
              }
            />
          </div>
        </div>
        {['importada', 'criada'].includes(layerType) && (
          <div className="filter-field">
            {filterColumn && (
              <div className={`values ${expandMobileValues && 'expanded'}`}>
                <button
                  className="expand-button"
                  onClick={() => setExpandMobileValues(!expandMobileValues)}
                >
                  <Icon i="map-attributetable-chevrondown" />
                </button>
                <div className="item-value red">
                  <p>{filterColumn}</p>
                </div>
                {Array.isArray(filterValues) &&
                  filterValues.map((fv, i) => (
                    <div className="item-value" key={i}>
                      <p>{fv}</p>
                    </div>
                  ))}
                {filterValues && typeof filterValues === 'string' && (
                  <div className="item-value">
                    <p>{filterValues}</p>
                  </div>
                )}
              </div>
            )}
            <div className="field">
              <MultiSelect
                type="radio"
                placeholder="Operador"
                name="operator_list"                
                value={                  
                  // if (!operatorsList.find((o) => o.value === filterOperator)) {
                  //   setFilterOperator(operatorsList[0].value)
                  //   return filterOperator
                  // } else {
                  //   return operatorsList[0].value
                  // }
                  operatorsList.find((o) => o.value === filterOperator)
                    ? filterOperator
                    : operatorsList[0].value && setFilterOperator(operatorsList[0].value)
                }
                onChange={(o) => {                    
                  setFilterOperator(o);
                }}
                data={
                  operatorsList
                }
              />
            </div>
          </div>
        )}
        <div className="filter-field">
          {filterColumn && (
            <div className={`values ${expandMobileValues && 'expanded'}`}>
              <button
                className="expand-button"
                onClick={() => setExpandMobileValues(!expandMobileValues)}
              >
                <Icon i="map-attributetable-chevrondown" />
              </button>
              <div className="item-value red">
                <p>{filterColumn}</p>
              </div>
              {Array.isArray(filterValues) &&
                filterValues.map((fv, i) => (
                  <div className="item-value" key={i}>
                    <p>{fv}</p>
                  </div>
                ))}
              {filterValues && typeof filterValues === 'string' && (
                <div className="item-value">
                  <p>{filterValues}</p>
                </div>
              )}
            </div>
          )}
          <div className="field">
            {filterOperator === 'IN' && (
              <MultiSelect
                type="checkbox"
                placeholder="Valor / Feição"
                name="feature-value"
                value={filterValues}
                onChange={(v) => setFilterValues(v)}
                data={filterData}
              />
            )}
            {(['LIKE', '=', '!==', '<', '<=', '>', '>='].includes(filterOperator)) && (
              <Input
                placeholder="Valor / Feição"
                name="feature-value"
                value={filterValues}
                fullWidth
                onChange={(e) => setFilterValues(e.target.value)}
                className="text-field"
              />
            )}
          </div>
        </div>
        <Button onClick={handleFilter} disabled={enabledFilter ? false : true}>Filtrar</Button>
      </div>
      <Mobile>
        <div style={{ width: '100%', textAlign: 'center' }}>
          <Button onClick={handleFilter}>Filtrar</Button>
        </div>
      </Mobile>
    </WrapFilter>
  );
};

Filter.properties = {
  ignore: true,
};

export default Filter;
